import { env } from '@monorepo/env'
import AsyncStorage from '@react-native-async-storage/async-storage'
import nodeFetch, { RequestInit } from 'node-fetch'
import { getContext } from '../getContext'

// Ensure AbortController is available (React Native 0.60+ has it, but add fallback for older versions)
if (typeof AbortController === 'undefined') {
  // @ts-ignore
  global.AbortController = require('abortcontroller-polyfill/dist/cjs-ponyfill').AbortController
}

const retrieveFromCache = async (key: string) => {
  try {
    const value = await AsyncStorage.getItem(key)
    return value ? JSON.parse(value) : null
  } catch (e) {
    console.error('Error retrieving from cache', e)
    return null
  }
}

type AuthFetchOptions = RequestInit & {
  cacheKey?: string
  onCacheHit?: (data: any) => void
  timeout?: number // Timeout in milliseconds
}

export const authFetch = async (url: string, options: AuthFetchOptions = {}) => {
  if (options.cacheKey && options.onCacheHit) {
    const key = `authFetch::${url}::${options.cacheKey}`
    const cachedData = await retrieveFromCache(key)
    if (cachedData) {
      options.onCacheHit(cachedData)
    }
  }

  const context = await getContext()
  const timeout = options.timeout || 120000 // Default 120 seconds (2 minutes)

  // Create AbortController for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await nodeFetch(url, {
      method: 'POST',
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        origin: env.EXPO_PUBLIC_WEBSITE,
        referer: env.EXPO_PUBLIC_WEBSITE,
        ...options.headers,
      },
      body: options.body
        ? JSON.stringify({
            ...JSON.parse(options.body as string),
            context,
          })
        : JSON.stringify({
            context,
          }),
    })
    if (!response.ok) {
      let errorBody: { code: number; message: string }

      try {
        errorBody = await response.json() // assumes server sends JSON like { code, message }
      } catch {
        // fallback in case it's not JSON
        errorBody = {
          code: response.status,
          message: response.statusText || 'Unknown error',
        }
      }

      // throw with your custom shape
      throw errorBody
    }

    const json = await response.json()

    if (options.cacheKey) {
      const key = `authFetch::${url}::${options.cacheKey}`
      await AsyncStorage.setItem(key, JSON.stringify(json))
    }

    return {
      ...response,
      json: () => Promise.resolve(json),
    }
  } catch (error: any) {
    // Check if error is due to timeout
    if (error.name === 'AbortError') {
      throw {
        code: 408,
        message: `Request timeout after ${timeout}ms`,
      }
    }
    // Re-throw other errors
    throw error
  } finally {
    // Always clear the timeout
    clearTimeout(timeoutId)
  }
}
